
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { LetterSubmission } from "@/types/letter-submissions"

export function useLetterSubmission(formData: any) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailResponse, setEmailResponse] = useState<any>(null)
  const [emailDebugInfo, setEmailDebugInfo] = useState<any>(null)
  const { toast } = useToast()

  const saveLetterSubmission = async (letterContent: string) => {
    const deliveryMethod = localStorage.getItem('selectedDeliveryMethod') || 'Unknown'
    const amountPaid = parseInt(localStorage.getItem('paymentAmount') || '0')

    try {
      console.log('Starting letter submission process')
      
      const submission: LetterSubmission = {
        sender_first_name: formData.sender.firstName,
        sender_last_name: formData.sender.lastName,
        sender_email: formData.sender.email,
        recipient_first_name: formData.recipient.firstName,
        recipient_last_name: formData.recipient.lastName,
        recipient_email: formData.recipient.email || null,
        recipient_street_address: formData.recipient.streetAddress || null,
        recipient_unit: formData.recipient.unit || null,
        recipient_city: formData.recipient.city || null,
        recipient_state: formData.recipient.state || null,
        recipient_zip_code: formData.recipient.zipCode || null,
        situation_description: formData.situation.description,
        letter_content: letterContent,
        delivery_method: deliveryMethod,
        amount_paid: amountPaid,
        payment_status: 'paid',
        status: 'submitted'
      }

      console.log('Saving letter submission with data:', submission)

      const { error, data } = await supabase
        .from('letter_submissions')
        .insert(submission)
        .select()

      if (error) {
        console.error('Supabase error saving submission:', error)
        throw error
      }

      console.log('Letter successfully saved to database:', data)
      return true
    } catch (error: any) {
      console.error('Error saving letter submission:', error)
      return false
    }
  }

  const sendConfirmationEmails = async (letterContent: string) => {
    setEmailSending(true)
    setEmailError(null)
    setEmailResponse(null)
    setEmailDebugInfo(null)
    
    try {
      const deliveryMethod = localStorage.getItem('selectedDeliveryMethod') || 'Unknown'
      const amountPaid = parseInt(localStorage.getItem('paymentAmount') || '0')
      
      console.log('Preparing confirmation and admin notification emails...');
      
      // 1. First send customer confirmation
      const customerPayload = {
        type: 'order_confirmation',
        data: {
          customerEmail: formData.sender.email,
          customerName: `${formData.sender.firstName} ${formData.sender.lastName}`,
          deliveryMethod: deliveryMethod,
          letterContent: letterContent,
        }
      };
      
      console.log('Sending customer confirmation email with payload:', JSON.stringify(customerPayload, null, 2));
      setEmailDebugInfo(prevDebug => ({ ...prevDebug, customerEmail: customerPayload }));
      
      const customerResponse = await supabase.functions.invoke('email-service', {
        body: customerPayload
      });

      console.log('Customer confirmation email result:', customerResponse);
      setEmailResponse(prevResp => ({ ...prevResp, customerEmail: customerResponse }));

      if (customerResponse.error) {
        console.error('Customer email error:', customerResponse.error);
        setEmailError(prev => `${prev ? prev + '; ' : ''}Customer email: ${customerResponse.error.message || 'Unknown error'}`);
      }
      
      // 2. Then send admin notification regardless of customer email success
      const adminPayload = {
        type: 'admin_notification',
        data: {
          adminEmail: 'teamdesistit@gmail.com',
          formData: formData,
          letterContent: letterContent,
          deliveryMethod: deliveryMethod,
          amount: amountPaid,
        }
      };
      
      console.log('Sending admin notification email with payload:', JSON.stringify(adminPayload, null, 2));
      setEmailDebugInfo(prevDebug => ({ ...prevDebug, adminEmail: adminPayload }));
      
      const adminResponse = await supabase.functions.invoke('email-service', {
        body: adminPayload
      });
      
      console.log('Admin notification email result:', adminResponse);
      setEmailResponse(prevResp => ({ ...prevResp, adminEmail: adminResponse }));

      if (adminResponse.error) {
        console.error('Admin email error:', adminResponse.error);
        setEmailError(prev => `${prev ? prev + '; ' : ''}Admin email: ${adminResponse.error.message || 'Unknown error'}`);
        return false;
      }
      
      return !customerResponse.error || !adminResponse.error;
    } catch (error: any) {
      console.error('Exception during email sending:', error);
      setEmailError(`Exception: ${error.message || 'Unknown error'}`);
      return false;
    } finally {
      setEmailSending(false);
    }
  }

  const handleSubmit = async (letterContent: string, onSuccess: () => void) => {
    setIsSubmitting(true)
    console.log('Starting letter submission process')
    
    try {
      console.log('Calling saveLetterSubmission with letter content')
      const saved = await saveLetterSubmission(letterContent)
      if (!saved) {
        throw new Error('Failed to save letter submission')
      }

      console.log('Letter saved successfully, now sending notification emails')
      
      const emailsSent = await sendConfirmationEmails(letterContent)
      if (!emailsSent) {
        console.warn('Failed to send some or all confirmation emails but continuing with success flow')
        console.log('Email response was:', emailResponse);
        
        toast({
          variant: "warning",
          title: "Email Notification",
          description: "Your letter has been submitted, but we couldn't send all confirmation emails. We'll still process your order.",
        })
      }

      toast({
        title: "Success",
        description: "Your letter has been submitted successfully!" + (emailsSent ? " Check your email for confirmation." : ""),
      })
      
      // Call the onSuccess callback instead of redirecting
      onSuccess()
    } catch (error: any) {
      console.error('Error submitting letter:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit letter. Please try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    isSubmitting,
    emailSending,
    emailError,
    emailResponse,
    emailDebugInfo,
    handleSubmit
  }
}
